import React from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Eye, Edit, Trash2, Lock, ShieldCheck, RotateCcw } from "lucide-react";

export const DefectRow = ({
  defect,
  contractors,
  isViewOnly,
  canCloseOut,
  getPriorityColor,
  onView,
  onEdit,
  onDelete,
  onCloseOut,
  onReopen,
  onStatusChange,
}) => {
  return (
    <tr
      className="hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={() => onView(defect)}
    >
      <td className="px-4 py-3 text-sm font-mono font-medium text-slate-900">
        {defect.number}
        {defect.client_visible && (
          <span className="block w-fit mt-1 whitespace-nowrap font-sans text-[10px] font-bold uppercase tracking-wide rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5"
            data-testid={`defect-client-badge-${defect.id}`}>
            {defect.added_after_walkthrough ? "Client · added after" : "Client walkthrough"}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{defect.area}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{defect.element}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{defect.issue}</td>
      <td className="px-4 py-3">
        <Badge className={`text-xs ${getPriorityColor(defect.priority)}`}>
          {defect.priority}
        </Badge>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {defect.closed_out ? (
          <div
            className="flex flex-col gap-1"
            data-testid={`defect-status-closed-${defect.id}`}
          >
            <Badge className="text-xs bg-green-100 text-green-700 w-fit">
              completed
            </Badge>
            <Badge className="text-xs bg-slate-800 text-white w-fit flex items-center gap-1">
              <Lock className="w-3 h-3" /> Closed Out
            </Badge>
          </div>
        ) : (
          <Select
            value={defect.status}
            onValueChange={(value) => onStatusChange(defect.id, value)}
          >
            <SelectTrigger
              className="h-8 w-32"
              data-testid={`defect-status-select-${defect.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {defect.assigned_to ? (
          contractors.find((c) => c.id === defect.assigned_to)?.name || "Unknown"
        ) : (
          <span className="text-slate-400">Unassigned</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {defect.due_date || <span className="text-slate-400">-</span>}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(defect)}
            data-testid={`defect-view-btn-${defect.id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          {defect.status === "completed" && !defect.closed_out && canCloseOut && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCloseOut(defect)}
              className="text-green-600 hover:text-green-700 border-green-200"
              data-testid={`defect-closeout-btn-${defect.id}`}
            >
              <ShieldCheck className="w-4 h-4 mr-1" />
              Close Out
            </Button>
          )}
          {defect.closed_out && canCloseOut && onReopen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onReopen(defect)}
              className="text-amber-700 hover:text-amber-800 border-amber-200"
              data-testid={`defect-reopen-btn-${defect.id}`}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reopen
            </Button>
          )}
          {!isViewOnly && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(defect)}
                className="text-blue-600 hover:text-blue-700 border-blue-200"
                data-testid={`defect-edit-btn-${defect.id}`}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(defect.id)}
                className="text-red-600 hover:text-red-700 border-red-200"
                data-testid={`defect-delete-btn-${defect.id}`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};
